import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, MapPin, Briefcase, Clock, DollarSign,
  ChevronRight, ArrowRight, Zap, Building2, Filter, X
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const JOB_TYPES = [
  { value: "all", label: "All Types" },
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
];

const TYPE_COLORS = {
  "full-time": { color: "#2ECC71", bg: "rgba(46,204,113,0.1)" },
  "part-time": { color: "#38bdf8", bg: "rgba(56,189,248,0.1)" },
  "contract":  { color: "#facc15", bg: "rgba(250,204,21,0.1)" },
  "remote":    { color: "#a78bfa", bg: "rgba(167,139,250,0.1)" },
  "hybrid":    { color: "#fb923c", bg: "rgba(251,146,60,0.1)" },
};

function salaryDisplay(job) {
  if (!job.salary_min && !job.salary_max) return null;
  const fmt = (n) => n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${n}`;
  const period = job.salary_period === "hour" ? "/hr" : "/yr";
  if (job.salary_min && job.salary_max) return `${fmt(job.salary_min)}–${fmt(job.salary_max)}${period}`;
  if (job.salary_min) return `${fmt(job.salary_min)}+${period}`;
  return `Up to ${fmt(job.salary_max)}${period}`;
}

function JobCard({ job }) {
  const typeStyle = TYPE_COLORS[job.job_type] || { color: "#64748b", bg: "rgba(100,116,139,0.1)" };
  const loc = job.locations?.[0];
  const salary = salaryDisplay(job);
  const daysLeft = job.expires_at
    ? Math.max(0, Math.ceil((new Date(job.expires_at) - new Date()) / 86400000))
    : null;

  return (
    <Link to={`/jobs/${job.id}`}
      className="group block rounded-2xl p-5 transition-all duration-200 hover:-translate-y-1"
      style={{
        background: "white",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 8px rgba(0,0,0,0.05)"
      }}
      data-testid={`job-card-${job.id}`}>
      <div className="flex items-start gap-4">
        {/* Logo */}
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
          style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}>
          {job.company_logo_url
            ? <img src={job.company_logo_url} alt={job.company_name} className="w-full h-full object-cover" />
            : <Building2 size={22} className="text-slate-400" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-bold text-[#0F172A] text-base group-hover:text-[#003366] transition-colors leading-snug">
                {job.title}
              </h3>
              <p className="text-slate-500 text-sm mt-0.5 font-medium">{job.company_name}</p>
            </div>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0"
              style={{ color: typeStyle.color, background: typeStyle.bg }}>
              {job.job_type}
            </span>
          </div>

          <div className="flex items-center gap-4 mt-2.5 flex-wrap">
            {loc && (
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <MapPin size={11} /> {loc.city ? `${loc.city}, ${loc.state}` : loc.zip_code}
                {job.locations?.length > 1 && <span className="text-slate-400"> +{job.locations.length - 1} more</span>}
              </span>
            )}
            {salary && (
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <DollarSign size={11} /> {salary}
              </span>
            )}
            {daysLeft !== null && daysLeft <= 14 && (
              <span className="flex items-center gap-1 text-xs text-amber-500 font-semibold">
                <Clock size={10} /> {daysLeft}d left
              </span>
            )}
          </div>

          <p className="text-slate-400 text-sm mt-2 line-clamp-2 leading-relaxed">
            {job.description?.slice(0, 160)}{job.description?.length > 160 ? "…" : ""}
          </p>

          <div className="flex items-center gap-1 mt-3 text-xs font-semibold text-[#003366] opacity-0 group-hover:opacity-100 transition-opacity">
            View Details <ChevronRight size={12} />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function JobsBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [zipFilter, setZipFilter] = useState(searchParams.get("zip") || "");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("");

  useEffect(() => { loadJobs(); }, [page, typeFilter, stateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (q) params.q = q;
      if (zipFilter) params.zip_code = zipFilter;
      if (typeFilter !== "all") params.job_type = typeFilter;
      if (stateFilter) params.state = stateFilter;
      const res = await axios.get(`${API}/jobs`, { params });
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadJobs();
  };

  return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      <SEOHead
        title="Job Board — Find Local Jobs Near You | NextDayRefund.com"
        description="Browse job openings from local businesses across all 50 states. Full-time, part-time, remote, and contract jobs. Updated daily."
        canonical="/jobs"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden py-14"
        style={{ background: "linear-gradient(135deg, #003366 0%, #00509e 100%)" }}>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.08) 1px,transparent 1px)",
          backgroundSize: "40px 40px"
        }} />
        <div className="section-container relative">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full"
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)" }}>
              <div className="w-2 h-2 rounded-full bg-[#2ECC71]" style={{ animation: "livePulse 1.8s infinite" }} />
              <span className="text-white text-xs font-bold tracking-widest uppercase">New Jobs Daily</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-3 leading-tight">
              Find Your Next<br />
              <span style={{
                background: "linear-gradient(135deg, #2ECC71, #38bdf8)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
              }}>Local Opportunity</span>
            </h1>
            <p className="text-blue-200 text-base max-w-xl mx-auto">
              {total > 0 ? `${total} open positions` : "New jobs posted daily"} from verified local businesses across all 50 states.
            </p>
          </div>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex gap-2 rounded-2xl p-2 shadow-2xl"
              style={{ background: "white" }}>
              <div className="flex items-center gap-2 flex-1 px-3">
                <Search size={16} className="text-slate-400 flex-shrink-0" />
                <input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Job title, company, keyword..."
                  className="flex-1 text-sm text-slate-700 outline-none bg-transparent placeholder-slate-400"
                  data-testid="job-search-input"
                />
              </div>
              <div className="flex items-center gap-2 px-3 border-l border-slate-100">
                <MapPin size={14} className="text-slate-400 flex-shrink-0" />
                <input
                  value={zipFilter}
                  onChange={e => setZipFilter(e.target.value)}
                  placeholder="ZIP code"
                  className="w-24 text-sm text-slate-700 outline-none bg-transparent placeholder-slate-400"
                  data-testid="job-zip-input"
                />
              </div>
              <button type="submit"
                className="font-bold px-5 py-2.5 rounded-xl text-sm transition-all hover:brightness-110 text-white"
                style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)" }}
                data-testid="job-search-btn">
                Search
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Filters + Listings */}
      <section className="section-container py-8">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Sidebar filters */}
          <aside className="lg:w-56 flex-shrink-0">
            <div className="sticky top-24 rounded-2xl p-4"
              style={{ background: "white", border: "1px solid #e2e8f0" }}>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5">
                <Filter size={11} /> Job Type
              </p>
              <div className="space-y-1">
                {JOB_TYPES.map(t => (
                  <button key={t.value}
                    onClick={() => { setTypeFilter(t.value); setPage(1); }}
                    data-testid={`filter-jobtype-${t.value}`}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all"
                    style={typeFilter === t.value
                      ? { background: "#003366", color: "white" }
                      : { color: "#475569" }}>
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="mt-5 pt-4" style={{ borderTop: "1px solid #f1f5f9" }}>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">State</p>
                <input
                  value={stateFilter}
                  onChange={e => setStateFilter(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="e.g. NY"
                  maxLength={2}
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:border-[#003366] transition-colors"
                  style={{ border: "1px solid #e2e8f0" }}
                  data-testid="filter-state-input"
                />
              </div>

              {(typeFilter !== "all" || stateFilter) && (
                <button
                  onClick={() => { setTypeFilter("all"); setStateFilter(""); setPage(1); }}
                  className="mt-3 w-full flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-slate-600 py-1.5 rounded-lg transition-colors"
                  style={{ border: "1px dashed #e2e8f0" }}>
                  <X size={10} /> Clear Filters
                </button>
              )}

              <div className="mt-5 pt-4" style={{ borderTop: "1px solid #f1f5f9" }}>
                <p className="text-xs font-bold text-slate-400 mb-2">Hiring?</p>
                <Link to="/post-a-job"
                  className="flex items-center justify-center gap-1.5 w-full font-bold py-2.5 rounded-xl text-sm text-white transition-all hover:brightness-110"
                  style={{ background: "linear-gradient(135deg, #003366, #00509e)" }}
                  data-testid="sidebar-post-job-btn">
                  <Zap size={13} /> Post a Job — $50
                </Link>
              </div>
            </div>
          </aside>

          {/* Jobs list */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-slate-500 text-sm">
                {loading ? "Loading..." : `${total} job${total !== 1 ? "s" : ""} found`}
              </p>
              <Link to="/post-a-job"
                className="flex items-center gap-2 font-bold px-4 py-2 rounded-xl text-sm text-white transition-all hover:scale-105"
                style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", boxShadow: "0 3px 16px rgba(46,204,113,0.3)" }}
                data-testid="post-job-cta-btn">
                <Briefcase size={13} /> Post a Job — $50/year
              </Link>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="w-8 h-8 border-2 border-[#003366] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-slate-400 text-sm">Loading jobs...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-20 rounded-2xl"
                style={{ border: "2px dashed #e2e8f0" }}>
                <Briefcase size={40} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 font-bold text-lg">No jobs found</p>
                <p className="text-slate-400 text-sm mt-1 mb-5">Be the first to post a job in this area!</p>
                <Link to="/post-a-job"
                  className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl text-white text-sm transition-all hover:scale-105"
                  style={{ background: "linear-gradient(135deg, #003366, #00509e)" }}>
                  Post a Job — $50/year <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {jobs.map(job => <JobCard key={job.id} job={job} />)}
                </div>
                {total > 20 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40"
                      style={{ border: "1px solid #e2e8f0" }}>
                      Previous
                    </button>
                    <span className="text-sm text-slate-500">Page {page}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={page * 20 >= total}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40"
                      style={{ border: "1px solid #e2e8f0" }}>
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
